<!-- app.vue -->
<template>
  <div>
    <NuxtPage />
    <CookieConsent />
  </div>
</template>

<script setup>
import { watch, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { setBatteryById, normalizeBatteryId } from '~/components/timeline/dataDate'

const route = useRoute()
const router = useRouter()
const { locale, setLocale } = useI18n({ useScope: 'global' })
const colorMode = useColorMode()

// ---- Theme Sync ----
onMounted(() => {
  const savedTheme = localStorage.getItem('user-theme')
  if (savedTheme) {
    colorMode.preference = savedTheme
  } else {
    colorMode.preference = 'light' // Default to light if no save
  }
})

watch(() => colorMode.preference, (newVal) => {
  localStorage.setItem('user-theme', newVal)
})

// ---- helpers ----
const toSingle = (v) => Array.isArray(v) ? v[0] : v

const normalizeLang = (raw) => {
  const s = String(toSingle(raw) || '').trim().toLowerCase()
  if (s.startsWith('th')) return 'th'
  if (s.startsWith('en')) return 'en'
  return 'en'
}
const applyLang = (raw) => {
  const lang = normalizeLang(raw)
  if (!lang) return
  if (locale.value !== lang) setLocale(lang)
  try { localStorage.setItem('lang', lang) } catch { }
}

// ---- watch: lang from query ----
watch(
  () => route.query.lang,
  (newLang) => {
    const hasLangInQuery = !!toSingle(newLang)
    if (hasLangInQuery) {
      applyLang(newLang)
      if (process.client) {
        const q = { ...route.query }
        delete q.lang
        router.replace({ path: route.path, query: q })
      }
    } else if (process.client) {
      const saved = localStorage.getItem('lang')
      if (saved) applyLang(saved)
    }
  },
  { immediate: true }
)

// ---- SEO & head ----
const seoTitle = computed(() => locale.value === 'th' ? 'Battery Passports | แพลตฟอร์มตรวจสอบประวัติแบตเตอรี่' : 'Battery Passports | Traceability & Circularity')
const seoDescription = computed(() => locale.value === 'th' ? 'ระบบตรวจสอบข้อมูลและประวัติการใช้งานแบตเตอรี่ (Digital Battery Passport)' : 'The digital platform for through Digital Battery Passports.')
const seoKeywords = computed(() => locale.value === 'th' ? 'แบตเตอรี่พาสปอร์ต, พาสปอร์ตแบตเตอรี่, ตรวจสอบประวัติแบตเตอรี่, แบตเตอรี่ EV, เศรษฐกิจหมุนเวียน, ความยั่งยืน, GridWhiz' : 'Battery Passport, Digital Battery Passport, Battery Traceability, Circular Economy, EV Battery, Sustainability, GridWhiz')
const seoImage = '/images/screenmockup.png'

useSeoMeta({
  title: seoTitle,
  ogTitle: seoTitle,
  description: seoDescription,
  ogDescription: seoDescription,
  ogImage: seoImage,
  ogType: 'website',
  twitterCard: 'summary_large_image',
  twitterTitle: seoTitle,
  twitterDescription: seoDescription,
  twitterImage: seoImage,
})

useHead(() => ({
  htmlAttrs: { lang: locale.value },
  meta: [
    { name: 'author', content: 'GridWhiz' },
    { name: 'publisher', content: 'GridWhiz' },
    { name: 'keywords', content: seoKeywords.value },
    { name: 'robots', content: 'index, follow' },
    { name: 'apple-mobile-web-app-title', content: 'Battery Passports' },
    { name: 'application-name', content: 'Battery Passports' },
    { name: 'theme-color', content: colorMode.preference === 'dark' ? '#1e293b' : '#f7fafc' }
  ],
  link: [
    {
      rel: 'stylesheet',
      href: 'https://fonts.googleapis.com/css2?family=Noto+Sans+Thai:wght@300;400;500;600;700&display=swap'
    },
    { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' },
    { rel: 'canonical', href: 'https://viewbattery.info/' }
  ],
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Battery Passports",
        "url": "https://viewbattery.info/",
        "description": "The digital platform for battery traceability and circular economy compliance.",
        "publisher": {
          "@type": "Organization",
          "name": "GridWhiz",
          "logo": {
            "@type": "ImageObject",
            "url": "https://viewbattery.info/images/gridwhiz-white.png"
          }
        }
      })
    }
  ]
}))
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Thai:wght@300;400;500;600;700&display=swap');

body {
  margin: 0;
  font-family: "Noto Sans Thai", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  background-color: #f7fafc;
  color: #2d3748;
}

/* Apply dark mode styles when the dark class is present */
.dark body {
  background-color: #1e293b;
  color: #f7fafc;
}

html[lang="th"] body {
  font-family: "Noto Sans Thai", "Sukhumvit Set", sans-serif;
  line-height: 1.6;
}

html[lang="en"] body {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  line-height: 1.5;
}
</style>
